/**
 * The seasonal/everyday split for the hub's occasion tiles (THE-819, design in
 * THE-816 PRD p.3-4 / p.10-11, canvas p03 / p11).
 *
 * `theaWebHubOccasions/current` is one flat, hand-authored list — the mapping
 * document does not say which of its entries are holidays. This module is
 * what decides that, and it decides it WITHOUT a schema change: an occasion's
 * `key` IS a holiday if, read as a holiday event type, it names one of the 26
 * entries `calendar/holidays.ts` already knows about. That keeps the no-deploy
 * guarantee `hubDocs.ts` already makes for the mapping — the board adds a
 * `halloween` occasion the same way it adds an `anniversary` one, and this
 * module is what notices the difference.
 *
 * WHY REUSE `calendar/holidays.ts` RATHER THAN DUPLICATE A DATE TABLE. Every
 * date rule a holiday needs — fixed, floating, lunar-table, year-boundary
 * wraparound — is already solved and already tested there. Re-deriving dates
 * here would be a second place those rules could drift from the Calendar's,
 * and `holidaysInRange` already answers exactly the question this module asks
 * ("which holidays fall in this window"), so it is called directly rather
 * than re-walked.
 *
 * WHY THE HIDDEN-HOLIDAY FILTER DOES DOUBLE DUTY FOR "LOADING". The PRD rule
 * is "show no seasonal tiles while settings load, rather than showing then
 * hiding" — and `holidayPrefs/hiddenHolidays.ts`'s `ready` input already means
 * exactly that: `holidayVisibility` returns `() => false` for every holiday
 * until `ready` is true. So passing `{ ready, hiddenHolidays }` straight
 * through to `filterHiddenHolidays` gives the loading state for free; there is
 * no second branch in here that says "if not ready, render nothing".
 */
import {
  holidaysInRange,
  isHolidayEventType,
  type HolidayEventType,
} from '../calendar/holidays';
import { holidayCountdownLabel } from '../calendar/holidayCountdown';
import { filterHiddenHolidays, type HolidayPrefsView } from '../holidayPrefs/hiddenHolidays';
import type { HubOccasionTile } from './hubDocs';

/**
 * A holiday gets a tile 8 weeks before its date (PRD p.3), and the tile
 * disappears the day after — so the window is `[0, 56]` days from today,
 * both ends inclusive.
 */
export const SEASONAL_WINDOW_DAYS = 56;

/**
 * An occasion's `key` -> the holiday it names, or `undefined` if it names
 * none. `HubOccasion.key` is hyphen-slugged (`hubDocs.ts`'s `slug()`
 * validator rejects underscores); `HolidayEventType` is snake_case
 * (`teacher_appreciation`). The hyphen->underscore swap is the same
 * translation `occasionRedirect.ts`'s `occasionTopicSlug` already does in the
 * opposite direction for the same two vocabularies, so an occasion keyed
 * `teacher-appreciation` is recognised as `teacher_appreciation` rather than
 * silently falling through to "everyday".
 */
export function occasionHolidayEventType(key: string): HolidayEventType | undefined {
  const candidate = key.replace(/-/g, '_');
  return isHolidayEventType(candidate) ? candidate : undefined;
}

/**
 * The countdown copy. Wording itself is `calendar/holidayCountdown`'s
 * `holidayCountdownLabel` — the one rule THE-1190 gives every tab that shows
 * a holiday countdown, not a hub-specific ladder — so this function's own
 * job is only the hub's own bound: `undefined` outside
 * `[0, SEASONAL_WINDOW_DAYS]`, a defensive second check alongside
 * `holidaysInRange`'s own window filter, so this function can never be asked
 * to describe a day it was not built for even if a future caller hands it
 * one directly.
 */
export function occasionCountdownLabel(days: number): string | undefined {
  if (days < 0 || days > SEASONAL_WINDOW_DAYS) return undefined;
  return holidayCountdownLabel(days);
}

/** A resolved occasion tile, for either row. `countdownLabel` present means
 *  seasonal (render the clock glyph + the label); absent means everyday
 *  (title only). */
export interface HubOccasionRowTile extends HubOccasionTile {
  countdownLabel?: string;
}

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whole days from the viewer's LOCAL today to a `YYYY-MM-DD` date, the same
 *  UTC-midnight-vs-local-midnight basis `comingUpEntries.daysFromIsoDate` uses
 *  on the calendar surface — duplicated rather than imported, the same
 *  per-surface split `HubOccasionCircles` already chose over forking
 *  `GuidePeekRail`: the two callers must not become coupled just because the
 *  arithmetic happens to match today. */
function daysUntilIso(dateIso: string, now: Date): number {
  const match = DAY_RE.exec(dateIso);
  if (!match) return NaN;
  const target = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86_400_000);
}

/**
 * Split + order the hub's resolved occasion tiles for the two-row grid
 * (PRD p.3-4: "Seasonal tiles first (with countdown), then the everyday list
 * in order").
 *
 * A tile whose key names a holiday that is NOT currently visible — outside
 * the 8-week window, hidden by the shopper's saved settings, or her settings
 * have not loaded yet — is DROPPED, not demoted to everyday: a "Halloween"
 * tile with no countdown would be a lie about what the tile is standing for.
 * Only a key that names no holiday at all is everyday.
 *
 * `tiles` is `resolveHubOccasionTiles`'s output, unchanged — this function
 * adds no second guarantee about guides; it only decides which row a
 * guide-backed tile belongs in.
 */
export function hubOccasionRows(
  tiles: readonly HubOccasionTile[],
  now: Date,
  holidayPrefs: HolidayPrefsView,
): HubOccasionRowTile[] {
  const windowEnd = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + SEASONAL_WINDOW_DAYS,
  );
  const visibleHolidays = filterHiddenHolidays(holidaysInRange(now, windowEnd), holidayPrefs);
  const dateByEventType = new Map(visibleHolidays.map((h) => [h.eventType, h.date]));

  const everyday: HubOccasionRowTile[] = [];
  const seasonal: Array<{ tile: HubOccasionRowTile; days: number }> = [];

  tiles.forEach((tile) => {
    const eventType = occasionHolidayEventType(tile.key);
    if (!eventType) {
      everyday.push(tile);
      return;
    }
    const date = dateByEventType.get(eventType);
    if (!date) return; // out of window, hidden, or not ready — no tile at all
    const days = daysUntilIso(date, now);
    const countdownLabel = occasionCountdownLabel(days);
    if (!countdownLabel) return;
    seasonal.push({ tile: { ...tile, countdownLabel }, days });
  });

  seasonal.sort((a, b) => a.days - b.days);
  return [...seasonal.map((s) => s.tile), ...everyday];
}
